import { makeAutoObservable } from 'mobx';
import { toast } from 'sonner';

import { UndoRedoActionType } from './undoRedoTypes';

import type {
  IUndoRedoManager,
  UndoRedoAction,
  UndoRedoCallable,
  UndoRedoGroup,
} from './undoRedoTypes';

/** Set to true to enable undo/redo flow logging. */
const LOG = true;

/**
 * A single named undo/redo history stack implementing the full `IUndoRedoManager` contract.
 *
 * Each entry in the history is a `UndoRedoGroup` — a named batch of one or more actions
 * that undo and redo together as a single step.
 *
 * Instances are managed by `UndoRedoManager`, which owns a keyed collection of stacks and
 * routes all editor operations to the currently active one. Can also be used standalone in
 * unit tests.
 */
export class UndoRedoStack implements IUndoRedoManager {
  history: UndoRedoGroup[] = [];
  currentGroupIndex = -1;
  currentAction: UndoRedoAction | null = null;
  openGroup: UndoRedoGroup | null = null;
  version = 0;
  _isCommittingAction = false;
  _isReplayingHistory = false;
  _maxSteps = 0;

  constructor() {
    makeAutoObservable(this);
  }

  // === Grouping ===

  beginGroup(name: string): void {
    if (this.openGroup) {
      console.warn('UndoRedo: beginGroup called while a group is already open. Nesting is not supported.');
      return;
    }
    if (LOG) console.log(`[UndoRedo] beginGroup "${name}"`);
    this.openGroup = { name, actions: [] };
  }

  endGroup(): void {
    if (!this.openGroup) {
      console.warn('UndoRedo: endGroup called without a matching beginGroup.');
      return;
    }
    const group = this.openGroup;
    this.openGroup = null;

    if (group.actions.length === 0) {
      if (LOG) console.log(`[UndoRedo] endGroup "${group.name}" — no actions, discarded`);
      return;
    }

    if (LOG) console.log(`[UndoRedo] endGroup "${group.name}" (${group.actions.length} actions)`);
    this._commitGroup(group);
  }

  // === Core Action Management ===

  /**
   * Records and executes a single undo/redo action in one call.
   *
   * @param name - Human-readable label shown in undo/redo toasts and history.
   * @param doMethod - Executed on commit (and again on redo). Must contain all stateful changes.
   * @param undoMethod - Executed on undo. Should fully reverse the effects of `doMethod`.
   * @param actionType - Defaults to `Primary`. Pass `Silent` to execute without entering history.
   */
  async recordAction(
    name: string,
    doMethod: UndoRedoCallable,
    undoMethod: UndoRedoCallable,
    actionType: UndoRedoActionType = UndoRedoActionType.Primary,
  ): Promise<void> {
    this.createAction(name, actionType);
    this.setDoMethod(doMethod);
    this.setUndoMethod(undoMethod);
    await this.commitAction(true);
  }

  /** @deprecated Use `recordAction` instead. */
  createAction(name: string, actionType: UndoRedoActionType = UndoRedoActionType.Primary): void {
    if (this.currentAction) {
      console.warn('UndoRedo: Cannot create action while another action is in progress. Call commitAction() first.');
      return;
    }
    this.currentAction = { name, actionType };
  }

  /** @deprecated Use `recordAction` instead. */
  async commitAction(execute = true): Promise<void> {
    if (!this.currentAction) {
      console.warn('UndoRedo: No action to commit. Call createAction() first.');
      return;
    }

    const action = this.currentAction;
    this.currentAction = null;

    if (action.actionType === UndoRedoActionType.Silent) {
      if (LOG) console.log(`[UndoRedo] commitAction "${action.name}" [silent]${execute ? '' : ' (no execute)'}`);
      if (execute) {
        this._isCommittingAction = true;
        try {
          await this._executeDoOperations(action);
        } finally {
          this._isCommittingAction = false;
        }
      }
      return;
    }

    // Determine group membership synchronously before any await, so that callers
    // who don't await commitAction() don't race with endGroup() / _commitGroup().
    const targetGroup = this.openGroup;
    const destination = targetGroup ? `→ group "${targetGroup.name}"` : '→ standalone group';
    if (LOG) console.log(`[UndoRedo] commitAction "${action.name}" [${action.actionType}] ${destination}${execute ? '' : ' (no execute)'}`);

    if (targetGroup) {
      // Enqueue into the open group before executing, so endGroup() sees it even if
      // the caller doesn't await this promise.
      targetGroup.actions.push(action);
    }

    if (execute) {
      this._isCommittingAction = true;
      try {
        await this._executeDoOperations(action);
      } finally {
        this._isCommittingAction = false;
      }
    }

    if (!targetGroup) {
      this._commitGroup({ name: action.name, actions: [action] });
    }
  }

  /** @deprecated Use `recordAction` instead. */
  setDoMethod(callable: UndoRedoCallable): void {
    if (!this.currentAction) {
      console.warn('UndoRedo: No action in progress. Call createAction() first.');
      return;
    }
    this.currentAction.doMethod = callable;
  }

  /** @deprecated Use `recordAction` instead. */
  setUndoMethod(callable: UndoRedoCallable): void {
    if (!this.currentAction) {
      console.warn('UndoRedo: No action in progress. Call createAction() first.');
      return;
    }
    this.currentAction.undoMethod = callable;
  }

  // === History Navigation ===

  async undo(): Promise<boolean> {
    if (!this.hasUndo()) {
      if (LOG) console.log('[UndoRedo] undo — nothing to undo');
      toast.info('Nothing to undo');
      return false;
    }

    const group = this.history[this.currentGroupIndex];
    const fromIndex = this.currentGroupIndex;
    this.currentGroupIndex--;

    if (LOG) console.log(`[UndoRedo] undo "${group.name}" [${group.actions.length} actions] index ${fromIndex} → ${this.currentGroupIndex}`);

    this._isReplayingHistory = true;
    this._isCommittingAction = true;
    try {
      for (const action of [...group.actions].reverse()) {
        if (LOG) console.log(`[UndoRedo]   ↩ "${action.name}"`);
        await this._executeUndoOperations(action);
      }

      this.version++;
      toast.success(`Undo: ${group.name}`);
      return true;
    } catch (error) {
      this.currentGroupIndex++;
      if (LOG) console.log(`[UndoRedo] undo FAILED for "${group.name}" — index restored to ${this.currentGroupIndex}`);
      toast.error('Failed to undo');
      throw error;
    } finally {
      this._isReplayingHistory = false;
      this._isCommittingAction = false;
    }
  }

  async redo(): Promise<boolean> {
    if (!this.hasRedo()) {
      if (LOG) console.log('[UndoRedo] redo — nothing to redo');
      toast.info('Nothing to redo');
      return false;
    }

    this.currentGroupIndex++;
    const group = this.history[this.currentGroupIndex];
    const toIndex = this.currentGroupIndex;

    if (LOG) console.log(`[UndoRedo] redo "${group.name}" [${group.actions.length} actions] index ${toIndex - 1} → ${toIndex}`);

    this._isReplayingHistory = true;
    this._isCommittingAction = true;
    try {
      for (const action of group.actions) {
        if (LOG) console.log(`[UndoRedo]   ↪ "${action.name}"`);
        await this._executeDoOperations(action);
      }

      this.version++;
      toast.success(`Redo: ${group.name}`);
      return true;
    } catch (error) {
      this.currentGroupIndex--;
      if (LOG) console.log(`[UndoRedo] redo FAILED for "${group.name}" — index restored to ${this.currentGroupIndex}`);
      toast.error('Failed to redo');
      throw error;
    } finally {
      this._isReplayingHistory = false;
      this._isCommittingAction = false;
    }
  }

  // === State Queries ===

  get isGroupOpen(): boolean {
    return this.openGroup !== null;
  }

  hasUndo(): boolean {
    return this.currentGroupIndex >= 0;
  }

  hasRedo(): boolean {
    return this.currentGroupIndex < this.history.length - 1;
  }

  isCommittingAction(): boolean {
    return this._isCommittingAction;
  }

  isReplayingHistory(): boolean {
    return this._isReplayingHistory;
  }

  // === History Management ===

  clearHistory(increaseVersion = true): void {
    if (LOG) console.log(`[UndoRedo] clearHistory (had ${this.history.length} groups)`);
    this.history = [];
    this.currentGroupIndex = -1;
    this.currentAction = null;
    this.openGroup = null;

    if (increaseVersion) {
      this.version++;
    }
  }

  getHistoryCount(): number {
    return this.history.length;
  }

  getCurrentAction(): number {
    return this.currentGroupIndex;
  }

  getCurrentActionName(): string {
    if (this.currentGroupIndex >= 0 && this.currentGroupIndex < this.history.length) {
      return this.history[this.currentGroupIndex].name;
    }
    return '';
  }

  getActionName(id: number): string {
    if (id >= 0 && id < this.history.length) {
      return this.history[id].name;
    }
    return '';
  }

  getVersion(): number {
    return this.version;
  }

  // === Max Steps ===

  get maxSteps(): number {
    return this._maxSteps;
  }

  set maxSteps(value: number) {
    this._maxSteps = Math.max(0, value);
    this._enforceMaxSteps();
  }

  // === Private Methods ===

  private _commitGroup(group: UndoRedoGroup): void {
    const truncated = this.history.length - (this.currentGroupIndex + 1);
    this.history = this.history.slice(0, this.currentGroupIndex + 1);
    this.history.push(group);
    this.currentGroupIndex = this.history.length - 1;
    this._enforceMaxSteps();

    if (LOG) {
      const truncatedNote = truncated > 0 ? `, truncated ${truncated} redo step(s)` : '';
      console.log(`[UndoRedo] committed "${group.name}" [${group.actions.length} actions] → index ${this.currentGroupIndex}/${this.history.length - 1}${truncatedNote}`);
    }

    this.version++;
  }

  private _enforceMaxSteps(): void {
    if (this._maxSteps <= 0) return;
    while (this.history.length > this._maxSteps) {
      this.history.shift();
      this.currentGroupIndex--;
    }
    this.currentGroupIndex = Math.max(-1, this.currentGroupIndex);
  }

  private async _executeDoOperations(action: UndoRedoAction): Promise<void> {
    if (action.doMethod) {
      await action.doMethod();
    }
  }

  private async _executeUndoOperations(action: UndoRedoAction): Promise<void> {
    if (action.undoMethod) {
      await action.undoMethod();
    }
  }
}
